import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useCallback, useEffect, useState } from "react";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { MdOutlineAddCard } from "react-icons/md";
import debounce from "lodash/debounce";
import useApi from "../hook/useApi";
import Paggination from "./Paggination";
import Loader from "./Loader/Loader";
import { PAGE_SIZE_OPTIONS } from "../constants/pageSizeOptions";
import LedgerModal from "./Accounts/LedgerModal";
import { formatCurrency, formatDateFull } from "../utils/helper";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import SearchableSelect from "./Common/SearchableSelect";

const SubadminListLayer = () => {
  const { AsyncGetApiCall, AsyncPatchApiCall } = useApi();
  const navigate = useNavigate();
  const [query, setQuery] = useState({
    pageNo: 1,
    limitVal: 50,
    search: "",
    isPagination: true,
    status: "",
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { subadminId } = useAuth();

  const setPageNo = (pageNo) => {
    setQuery((prev) => ({
      ...prev,
      pageNo,
    }));
  };

  const getAllSubadminList = async () => {
    setLoading(true);
    const result = await AsyncGetApiCall("/subadmin/list", query);

    if (result.success) {
      setData(result);
    }
    setLoading(false);
  };

  const debouncedSearch = useCallback(
    debounce((value) => {
      setQuery((prev) => ({ ...prev, search: value, pageNo: 1 }));
    }, 500),
    [],
  );

  const handleSearch = (e) => {
    debouncedSearch(e.target.value);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
  };

  useEffect(() => {
    getAllSubadminList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const boolLabel = (v) => (v ? "Yes" : "No");

  const handleLedgerClick = (user) => {
    console.log("user", user);
    setSelectedAccount({
      name: user.masterName,
      id: user.loginId,
      dbId: user._id,
    });
    setIsModalOpen(true);
  };

  const toggleStatus = async (id, status) => {
    const { isConfirmed } = await Swal.fire({
      title: "Change Status?",
      text: `${status ? "Deactivate" : "Activate"} this subadmin?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2352c3",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Change it!",
      customClass: { popup: "swal-sm" },
    });

    if (!isConfirmed) return;
    setLoading(true);
    const payload = {
      id: id,
      isActive: status ? false : true,
    };
    const result = await AsyncPatchApiCall(`/subadmin/update`, payload);

    if (result?.errors) {
      toast.error(result?.errors[0]);
    } else if (result?.success) {
      toast.success(`Status updated successfully`);
      getAllSubadminList();
    }
    setLoading(false);
  };

  return (
    <>
      {loading && <Loader />}
      <div className="card h-100 p-0 radius-12">
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
          <div className="d-flex align-items-center justify-content-between gap-3 w-100">
            <div className="d-flex align-items-center gap-3">
              <span className="text-md fw-medium text-secondary-light mb-0">
                Show
              </span>
              <div style={{ minWidth: "92px" }}>
                <SearchableSelect
                  placeholder="Show"
                  value={String(query.limitVal)}
                  showSearch={false}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    setQuery((prev) => ({
                      ...prev,
                      limitVal: value,
                      pageNo: 1,
                    }));
                  }}
                  options={PAGE_SIZE_OPTIONS.map((n) => ({
                    label: String(n),
                    value: String(n),
                  }))}
                />
              </div>
              <form className="navbar-search" onSubmit={handleFormSubmit}>
                <input
                  type="text"
                  className="bg-base h-40-px w-600-px"
                  name="search"
                  placeholder="Search by login ID, sub-admin ID, master name, remarks"
                  onChange={handleSearch}
                />
                <Icon icon="ion:search-outline" className="icon" />
              </form>
              <span className="text-md fw-medium text-secondary-light mb-0">
                Status
              </span>
              <select
                name="status"
                id="status"
                className="form-select h-40-px"
                value={query.status}
                onChange={(e) => {
                  const value = e.target.value;
                  setQuery((prev) => ({ ...prev, status: value, pageNo: 1 }));
                }}
              >
                <option value="">All</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
            <div>
              <button
                type="button"
                className="btn btn-primary-600 radius-8 px-17 py-9 d-flex align-items-center gap-2"
                onClick={() => navigate("/subadmin-create")}
                disabled={loading}
              >
                <MdOutlineAddCard className="text-xl" />
                Add Subadmin
              </button>
            </div>
          </div>
        </div>

        <div className="card-body p-24">
          <div className="table-responsive scroll-sm">
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr>
                  <th scope="col">
                    <div className="d-flex align-items-center gap-10">
                      <div className="form-check style-check d-flex align-items-center" />
                      S.No
                    </div>
                  </th>
                  <th scope="col">Login ID</th>
                  <th scope="col">Master name</th>
                  <th scope="col">Current Balance</th>
                  <th scope="col">Remarks</th>
                  <th scope="col">Partnership</th>
                  <th scope="col">Only position</th>
                  <th scope="col">Limit orders</th>
                  <th scope="col">Created at</th>
                  <th scope="col">Status</th>
                  <th scope="col" className="text-center">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {data && data.data?.length > 0 ? (
                  data.data.map((subadmin, index) => (
                    <tr key={subadmin._id}>
                      <td>
                        <div className="d-flex justify-content-center align-items-center gap-10">
                          {(data?.currentPage - 1) * query.limitVal + index + 1}
                        </div>
                      </td>
                      <td>
                        <span className="text-md mb-0 fw-normal text-secondary-light">
                          {subadmin.loginId ?? "N/A"}
                        </span>
                      </td>
                      <td>
                        <span className="text-md mb-0 fw-normal text-secondary-light">
                          {subadmin.masterName ?? "N/A"}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex flex-column">
                          <span className="text-md fw-medium text-secondary-light" title="Real Balance">
                            {formatCurrency(subadmin?.currentBalance) || "0.00"}
                          </span>
                          {(subadmin?.unsettledPnl !== 0 && subadmin?.unsettledPnl !== undefined) && (
                            <span 
                              className={`text-xs fw-semibold ${subadmin?.unsettledPnl >= 0 ? "text-success-600" : "text-danger-600"}`} 
                              title="Unsettled Amount (Running PnL + Brokerage)"
                            >
                              Unsettled: {formatCurrency(subadmin?.unsettledPnl)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span
                          className="text-md mb-0 fw-normal text-secondary-light text-truncate d-inline-block"
                          style={{ maxWidth: "160px" }}
                          title={subadmin.remarks || ""}
                        >
                          {subadmin.remarks?.trim() ? subadmin.remarks : "—"}
                        </span>
                      </td>
                      <td>
                        <span className="text-md mb-0 fw-normal text-secondary-light">
                          {subadmin.partnership != null
                            ? `${subadmin.partnership}%`
                            : "N/A"}
                        </span>
                      </td>
                      <td>{boolLabel(subadmin.onlyPosition)}</td>
                      <td>{boolLabel(subadmin.limitOrders)}</td>
                      <td>{formatDateFull(subadmin.createdAt)}</td>
                      <td>
                        <span
                          className={`${subadmin.isActive
                            ? "bg-success-focus text-success-600"
                            : "bg-danger-focus text-danger-600"
                            } border px-16 py-4 radius-4 fw-medium text-sm ${!subadminId && "cursor-pointer"}`}
                          onClick={() =>
                            !subadminId &&
                            toggleStatus(subadmin._id, subadmin.isActive)
                          }
                        >
                          {subadmin.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="text-center">
                        <div className="d-flex justify-content-center align-items-center gap-2">
                          <button
                            type="button"
                            className="ledger-action-btn w-40-px h-40-px"
                            onClick={() => handleLedgerClick(subadmin)}
                            aria-label="View ledger"
                          >
                            <Icon icon="tabler:file-text" className="icon" />
                          </button>
                          <button
                            type="button"
                            title="View"
                            className="bg-info-focus text-info-600 bg-hover-info-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                            onClick={() =>
                              navigate(`/subadmin-view/${subadmin._id}`)
                            }
                          >
                            <Icon
                              icon="majesticons:eye-line"
                              className="icon"
                            />
                          </button>
                          <button
                            type="button"
                            title="Edit"
                            className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                            onClick={() =>
                              navigate(`/subadmin-update/${subadmin._id}`, {
                                state: { subadmin },
                              })
                            }
                          >
                            <Icon icon="lucide:edit" className="menu-icon" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={10} className="text-center">
                      No subadmins found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <Paggination
            total={data?.total || 0}
            totalPages={data?.totalPages || 0}
            currentPage={data?.currentPage || 0}
            setPageNo={setPageNo}
            startFrom={(data?.currentPage - 1) * query.limitVal + 1}
            endTo={
              (data?.currentPage - 1) * query.limitVal +
              (data?.data?.length || 0)
            }
          />
          {selectedAccount && (
            <LedgerModal
              show={!!selectedAccount && isModalOpen}
              onClose={() => {
                setIsModalOpen(false);
                setSelectedAccount(null);
              }}
              accountName={selectedAccount?.name ?? ""}
              accountId={selectedAccount?.id ?? ""}
              accountDbId={selectedAccount?.dbId}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default SubadminListLayer;
